/**
 * EditClientSheet Component (Stories 3.3, 3.4, 3.6)
 *
 * Modal dialog for editing an existing client with Framer Motion animations.
 * Pre-fills from client prop, supports dirty state tracking,
 * optimistic concurrency control, and unsaved changes guard.
 *
 * Story 3.4: Includes logo upload area (above name field) for Owner/Admin.
 * Logo upload is INDEPENDENT of form save — uploading does NOT dirty the form.
 *
 * Story 3.6: Includes tag management section below industry field.
 * Tag mutations are INDEPENDENT of form save — assigning/removing tags
 * calls the API immediately (same pattern as logo upload).
 */

import { useState, useRef, useEffect, useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  Button,
  Input,
} from "@provenleap/ui";
import { motion, AnimatePresence } from "motion/react";
import { Trash } from "@phosphor-icons/react";
import { apiPatch, apiDelete } from "../../lib/apiClient";
import { usePermission, Actions } from "../../hooks/usePermission";
import { ClientLogoUpload } from "./ClientLogoUpload";
import { ClientAvatar } from "./ClientAvatar";
import { TagPill } from "./TagPill";
import { TagPicker } from "./TagPicker";
import { IndustryPicker } from "./IndustryPicker";
import { BrandSelectField } from "./BrandSelectField";
import { useBrandMutations } from "../../hooks/useBrands";
import type { Client, ClientTag } from "./types";

interface EditClientSheetProps {
  client: Client | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  triggerRef?: React.RefObject<HTMLButtonElement | null>;
}



function EditClientSheet({ client, open, onOpenChange, triggerRef }: EditClientSheetProps) {
  const queryClient = useQueryClient();
  const { hasPermission } = usePermission();
  const canManageClients = hasPermission(Actions.MANAGE_CLIENTS);

  // Form state
  const [name, setName] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [industry, setIndustry] = useState("");
  // Owning brand — "" = no brand. Persisted on save via the brand assign/unassign
  // endpoints (the client PATCH does not carry brand_id).
  const [brandId, setBrandId] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { assignClients, unassignClient } = useBrandMutations();

  // Logo state — independent of form dirty tracking
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [clientUpdatedAt, setClientUpdatedAt] = useState("");

  // Tag state — independent of form dirty tracking (Story 3.6)
  const [tags, setTags] = useState<ClientTag[]>([]);

  // Discard dialog state
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);

  // Toast state
  const [toast, setToast] = useState<{ type: "success" | "error" | "info"; message: string } | null>(null);
  const toastTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Clear toast timeout on unmount
  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);

  // Pre-fill form from client prop
  useEffect(() => {
    if (client && open) {
      setName(client.name);
      setContactName(client.contactName ?? "");
      setContactEmail(client.contactEmail ?? "");
      setIndustry(client.industry ?? "");
      setBrandId(client.brandId ?? "");
      setLogoUrl(client.logoUrl);
      setClientUpdatedAt(client.updatedAt);
      setTags(client.tags ?? []);
      setErrors({});
    }
  }, [client, open]);

  // Dirty state tracking
  const isDirty = useMemo(() => {
    if (!client) return false;
    // "none" maps to null on submit, so compare accordingly
    const effectiveIndustry = industry === "none" ? "" : industry;
    return (
      name !== client.name ||
      contactName !== (client.contactName ?? "") ||
      contactEmail !== (client.contactEmail ?? "") ||
      effectiveIndustry !== (client.industry ?? "") ||
      brandId !== (client.brandId ?? "")
    );
  }, [name, contactName, contactEmail, industry, brandId, client]);

  function showToast(type: "success" | "error" | "info", message: string) {
    setToast({ type, message });
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    // Error toasts persist until manually dismissed (UX spec requirement)
    if (type !== "error") {
      toastTimeoutRef.current = setTimeout(() => setToast(null), 3000);
    }
  }

  function resetForm() {
    setName("");
    setContactName("");
    setContactEmail("");
    setIndustry("");
    setBrandId("");
    setErrors({});
    setShowDiscardDialog(false);
  }

  function handleClose() {
    resetForm();
    onOpenChange(false);
    triggerRef?.current?.focus();
  }

  function handleOpenChange(newOpen: boolean) {
    if (!newOpen) {
      if (isDirty) {
        setShowDiscardDialog(true);
        return;
      }
      handleClose();
    } else {
      onOpenChange(newOpen);
    }
  }

  // Validation
  function validate(): boolean {
    const newErrors: Record<string, string> = {};
    const trimmedName = name.trim();

    if (!trimmedName) {
      newErrors.name = "Client name is required";
    } else if (trimmedName.length > 100) {
      newErrors.name = "Client name must be 100 characters or less";
    }

    if (contactEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail.trim())) {
      newErrors.contactEmail = "Invalid email format";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  // Mutation
  const updateClient = useMutation({
    mutationFn: async (data: {
      id: string;
      name: string;
      contactName?: string | null;
      contactEmail?: string | null;
      industry?: string | null;
      updatedAt: string;
    }) => {
      const { id, ...body } = data;
      const res = await apiPatch(`/api/clients/${id}`, body);
      if (!res.ok) {
        const err = await res.json();
        if (res.status === 409) {
          throw new Error("Client was modified by another user. Please refresh.");
        }
        const detail =
          err.errors?.[0]?.message ?? err.detail ?? "Failed to update client";
        throw new Error(detail);
      }
      return res.json();
    },
    onMutate: async (updatedData) => {
      await queryClient.cancelQueries({ queryKey: ["clients"] });
      const previousClients = queryClient.getQueryData(["clients"]);
      queryClient.setQueryData(
        ["clients"],
        (old: Client[] | undefined) =>
          old?.map((c) =>
            c.id === updatedData.id
              ? {
                  ...c,
                  name: updatedData.name,
                  contactName: updatedData.contactName !== undefined ? updatedData.contactName : c.contactName,
                  contactEmail: updatedData.contactEmail !== undefined ? updatedData.contactEmail : c.contactEmail,
                  industry: updatedData.industry !== undefined ? updatedData.industry : c.industry,
                  updatedAt: new Date().toISOString(),
                }
              : c
          )
      );
      return { previousClients };
    },
    onSuccess: () => {
      resetForm();
      onOpenChange(false);
      showToast("success", `Changes to ${client?.name} saved`);
      triggerRef?.current?.focus();
    },
    onError: (err: Error, _vars, context) => {
      if (context?.previousClients) {
        queryClient.setQueryData(["clients"], context.previousClients);
      }
      showToast("error", err.message);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!client || !validate()) return;

    const data: {
      id: string;
      name: string;
      contactName?: string | null;
      contactEmail?: string | null;
      industry?: string | null;
      updatedAt: string;
    } = {
      id: client.id,
      name: name.trim(),
      updatedAt: clientUpdatedAt,
    };

    // Send null when field is emptied to clear it
    data.contactName = contactName.trim() || null;
    data.contactEmail = contactEmail.trim() || null;
    // "none" is the deselect option — convert to null
    data.industry = industry && industry !== "none" ? industry : null;

    // Brand membership lives on its own endpoints (not the client PATCH). If it
    // changed, assign to the new brand (or unassign from the old) — both invalidate
    // ["clients"] so the detail/list refresh. The toast/close come from updateClient.
    const oldBrandId = client.brandId ?? "";
    if (brandId !== oldBrandId) {
      const onBrandError = (err: unknown) =>
        showToast("error", (err as { message?: string })?.message ?? "Failed to update brand");
      if (brandId) {
        assignClients.mutate({ brandId, clientIds: [client.id] }, { onError: onBrandError });
      } else if (oldBrandId) {
        unassignClient.mutate({ brandId: oldBrandId, clientId: client.id }, { onError: onBrandError });
      }
    }

    updateClient.mutate(data);
  }

  // Tag removal — optimistic UI with rollback on error (Story 3.6)
  async function handleRemoveTag(tagId: string) {
    const previousTags = tags;
    setTags((prev) => prev.filter((t) => t.id !== tagId));
    try {
      const res = await apiDelete(`/api/clients/${client!.id}/tags/${tagId}`);
      if (!res.ok) {
        setTags(previousTags);
        showToast("error", "Failed to remove tag");
        return;
      }
      showToast("success", "Tag removed");
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["tags"] });
    } catch {
      setTags(previousTags);
      showToast("error", "Failed to remove tag");
    }
  }

  return (
    <>
      <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
        <AnimatePresence>
          {open && (
            <DialogPrimitive.Portal forceMount>
              {/* Overlay */}
              <DialogPrimitive.Overlay asChild forceMount>
                <motion.div
                  className="fixed inset-0 z-50 bg-black/35"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.25, ease: "easeOut" }}
                />
              </DialogPrimitive.Overlay>

              {/* Content */}
              <DialogPrimitive.Content
                asChild
                forceMount
                onEscapeKeyDown={(e: KeyboardEvent) => {
                  if (isDirty) {
                    e.preventDefault();
                    setShowDiscardDialog(true);
                  }
                }}
                onInteractOutside={(e: Event) => {
                  if (isDirty) {
                    e.preventDefault();
                    setShowDiscardDialog(true);
                  }
                }}
              >
                <motion.div
                  className="fixed left-1/2 top-1/2 z-50 flex h-[95vh] max-h-[95vh] w-full max-w-lg -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-3xl border-[0.5px] border-input bg-background px-8 pt-6 pb-6 shadow-lg"
                  initial={{ opacity: 0, scale: 0.95, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 10 }}
                  transition={{ duration: 0.25, ease: "easeOut" }}
                >
                  <div className="flex shrink-0 flex-col space-y-1.5 text-left">
                    <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
                      Edit {client?.name}
                    </DialogPrimitive.Title>
                    <DialogPrimitive.Description className="text-sm text-muted-foreground">
                      Update client details. Click save when you&apos;re done.
                    </DialogPrimitive.Description>
                  </div>

                  <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col mt-4">
                    {/* Scrollable field area — keeps the modal a fixed height so it
                        doesn't grow as fields are added; header + footer stay pinned. */}
                    <div className="flex-1 space-y-3 overflow-y-auto pr-1">
                    {/* Client Logo (Story 3.4) */}
                    {client && (
                      <motion.div
                        className="flex justify-center pb-2"
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.05 }}
                      >
                        {canManageClients ? (
                          <ClientLogoUpload
                            clientId={client.id}
                            clientName={client.name}
                            currentUrl={logoUrl}
                            updatedAt={clientUpdatedAt}
                            onUploadComplete={(newUrl, newUpdatedAt) => {
                              setLogoUrl(newUrl);
                              setClientUpdatedAt(newUpdatedAt);
                              queryClient.invalidateQueries({ queryKey: ["clients"] });
                            }}
                            onRemoveComplete={(newUpdatedAt) => {
                              setLogoUrl(null);
                              setClientUpdatedAt(newUpdatedAt);
                              queryClient.invalidateQueries({ queryKey: ["clients"] });
                            }}
                            onError={(message) => showToast("error", message)}
                          />
                        ) : (
                          <ClientAvatar
                            name={client.name}
                            logoUrl={logoUrl}
                            size="lg"
                          />
                        )}
                      </motion.div>
                    )}

                    {/* Client Name (required) */}
                    <motion.div
                      className="space-y-2"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 }}
                    >
                      <label htmlFor="edit-client-name" className="text-sm font-medium">
                        Client Name <span className="text-destructive">*</span>
                      </label>
                      <Input
                        id="edit-client-name"
                        value={name}
                        onChange={(e) => {
                          setName(e.target.value);
                          if (errors.name) setErrors((prev) => ({ ...prev, name: "" }));
                        }}
                        onBlur={() => {
                          if (!name.trim()) {
                            setErrors((prev) => ({ ...prev, name: "Client name is required" }));
                          }
                        }}
                        placeholder="e.g. Acme Corp"
                        className={`rounded-lg ${errors.name ? "border-destructive" : ""}`}
                        maxLength={100}
                      />
                      {errors.name && (
                        <p className="text-sm text-destructive" role="alert">
                          {errors.name}
                        </p>
                      )}
                    </motion.div>

                    {/* Contact Name (optional) */}
                    <motion.div
                      className="space-y-2"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.15 }}
                    >
                      <label htmlFor="edit-contact-name" className="text-sm font-medium">
                        Contact Name
                      </label>
                      <Input
                        id="edit-contact-name"
                        value={contactName}
                        onChange={(e) => setContactName(e.target.value)}
                        placeholder="e.g. John Doe"
                        maxLength={255}
                        className="rounded-lg"
                      />
                    </motion.div>

                    {/* Contact Email (optional) */}
                    <motion.div
                      className="space-y-2"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                    >
                      <label htmlFor="edit-contact-email" className="text-sm font-medium">
                        Contact Email
                      </label>
                      <Input
                        id="edit-contact-email"
                        type="email"
                        value={contactEmail}
                        onChange={(e) => {
                          setContactEmail(e.target.value);
                          if (errors.contactEmail)
                            setErrors((prev) => ({ ...prev, contactEmail: "" }));
                        }}
                        onBlur={() => {
                          if (
                            contactEmail.trim() &&
                            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail.trim())
                          ) {
                            setErrors((prev) => ({
                              ...prev,
                              contactEmail: "Invalid email format",
                            }));
                          }
                        }}
                        placeholder="e.g. john@acme.com"
                        className={`rounded-lg ${errors.contactEmail ? "border-destructive" : ""}`}
                      />
                      {errors.contactEmail && (
                        <p className="text-sm text-destructive" role="alert">
                          {errors.contactEmail}
                        </p>
                      )}
                    </motion.div>

                    {/* Industry (optional dropdown with None option) */}
                    <motion.div
                      className="space-y-2"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.25 }}
                    >
                      <label htmlFor="edit-industry" className="text-sm font-medium">
                        Industry
                      </label>
                      <IndustryPicker id="edit-industry" value={industry} onChange={setIndustry} />
                    </motion.div>

                    {/* Brand (optional) — groups this client for multi-client roll-ups. */}
                    <motion.div
                      className="space-y-2"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.27 }}
                    >
                      <label className="text-sm font-medium">Brand</label>
                      <BrandSelectField
                        value={brandId}
                        onChange={setBrandId}
                        showToast={showToast}
                        disabled={!canManageClients}
                      />
                    </motion.div>

                    {/* Tags (Story 3.6) — independent of form save */}
                    {client && (
                      <motion.div
                        className="space-y-2"
                        data-testid="tag-section"
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                      >
                        <label className="text-sm font-medium">Tags</label>
                        <div className="flex flex-wrap items-center gap-1.5">
                          {tags
                            .slice()
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((tag) => (
                              <TagPill
                                key={tag.id}
                                tag={tag}
                                size="sm"
                                onRemove={
                                  canManageClients
                                    ? () => handleRemoveTag(tag.id)
                                    : undefined
                                }
                              />
                            ))}
                          {tags.length === 0 && (
                            <p className="text-sm text-muted-foreground">No tags assigned</p>
                          )}
                          {canManageClients && (
                            <TagPicker
                              clientId={client.id}
                              currentTags={tags}
                              onTagAssigned={(tag) => {
                                setTags((prev) => [...prev, tag]);
                              }}
                              onTagRemoved={(tagId) => {
                                setTags((prev) => prev.filter((t) => t.id !== tagId));
                              }}
                              onToast={showToast}
                            />
                          )}
                        </div>
                      </motion.div>
                    )}
                    </div>

                    <motion.div
                      className="shrink-0"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.35 }}
                    >
                      <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 mt-4">
                        <Button
                          type="button"
                          variant="ghost"
                          className="rounded-xl bg-background cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all"
                          style={{ border: "0.5px solid hsl(var(--input))" }}
                          onClick={() => handleOpenChange(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          variant="brand"
                          className="rounded-xl cursor-pointer"
                          disabled={!name.trim() || updateClient.isPending}
                        >
                          {updateClient.isPending ? "Saving..." : "Save Changes"}
                        </Button>
                      </div>
                    </motion.div>
                  </form>
                </motion.div>
              </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
          )}
        </AnimatePresence>
      </DialogPrimitive.Root>

      {/* Unsaved changes confirmation dialog */}
      <DialogPrimitive.Root open={showDiscardDialog} onOpenChange={setShowDiscardDialog}>
        <AnimatePresence>
          {showDiscardDialog && (
            <DialogPrimitive.Portal forceMount>
              <DialogPrimitive.Overlay asChild forceMount>
                <motion.div
                  className="fixed inset-0 z-[60] bg-black/35"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.25, ease: "easeOut" }}
                />
              </DialogPrimitive.Overlay>
              <DialogPrimitive.Content asChild forceMount>
                <motion.div
                  className="fixed left-1/2 top-1/2 z-[60] w-full max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-3xl border-[0.5px] border-input bg-background p-8 shadow-lg outline-none"
                  initial={{ opacity: 0, scale: 0.95, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 10 }}
                  transition={{ duration: 0.25, ease: "easeOut" }}
                >
                  <div className="flex flex-col space-y-1.5 text-left">
                    <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
                      Unsaved changes
                    </DialogPrimitive.Title>
                    <DialogPrimitive.Description className="text-sm text-muted-foreground">
                      You have unsaved changes that will be lost.
                    </DialogPrimitive.Description>
                  </div>
                  <div className="flex flex-col-reverse sm:flex-row sm:justify-center sm:space-x-2 mt-6">
                    <Button
                      variant="outline"
                      className="rounded-xl cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all"
                      onClick={() => setShowDiscardDialog(false)}
                      autoFocus
                    >
                      Keep editing
                    </Button>
                    <Button
                      variant="destructive"
                      className="rounded-xl cursor-pointer hover:-translate-y-0.5 hover:shadow-sm transition-all"
                      onClick={handleClose}
                    >
                      <Trash size={16} className="mr-1" />
                      Discard changes
                    </Button>
                  </div>
                </motion.div>
              </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
          )}
        </AnimatePresence>
      </DialogPrimitive.Root>

      {/* Toast notification */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.2 }}
            className={`fixed bottom-4 right-4 z-50 rounded-xl px-4 py-3 text-sm shadow-lg flex items-center gap-2 ${
              toast.type === "success"
                ? "bg-green-50 border border-green-200 text-green-800"
                : toast.type === "info"
                  ? "bg-blue-50 border border-blue-200 text-blue-800"
                  : "bg-destructive/10 border border-destructive/20 text-destructive"
            }`}
            role={toast.type === "error" ? "alert" : "status"}
          >
            {toast.message}
            {toast.type === "error" && (
              <button
                type="button"
                onClick={() => setToast(null)}
                className="ml-2 font-medium underline text-xs"
                aria-label="Dismiss error"
              >
                Dismiss
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export { EditClientSheet };
